import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type { AxiosError } from "axios";
import { toast } from "sonner";
import { AlertCircle, ChevronsUpDown, Info, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import {
  MAX_PUBLIC_SCOPE_KEYS,
  useKBConfig,
  useKBIntegrations,
  useKBScopeKeys,
  useKBSources,
  useUpdateKBConfig,
  type KBConfig,
  type SourceAccessMode,
} from "@/hooks/use-kb-config";

/** Provider slug → display label for the per-source access section. */
const PROVIDER_LABELS: Record<string, string> = {
  confluence: "Confluence",
  "confluence-api-token": "Confluence",
  "microsoft-sharepoint": "SharePoint",
  simpplr: "Simpplr",
  workvivo: "Workvivo",
  freshservice: "Freshservice",
  "google-drive": "Google Drive",
  guru: "Guru",
};

/** Tri-state per-source value: inherit the tenant default, or override it. */
type SourceAccessChoice = "inherit" | SourceAccessMode;

const searchSchema = z.object({
  tenantId: z.string().optional(),
});

export const Route = createFileRoute("/kb-config")({
  component: KBConfigPage,
  validateSearch: zodValidator(searchSchema),
});

/** Serialize a map with sorted keys so object key order can't read as a diff. */
function normalizeSourceModes(
  modes: Record<string, SourceAccessMode>,
): [string, SourceAccessMode][] {
  return Object.entries(modes).sort(([a], [b]) => a.localeCompare(b));
}

/** Order-insensitive comparison of two configs (map/array order is irrelevant). */
function sameConfig(a: KBConfig, b: KBConfig): boolean {
  const norm = (c: KBConfig) =>
    JSON.stringify({
      ...c,
      public_scope_keys: [...c.public_scope_keys].sort(),
      source_access_modes: normalizeSourceModes(c.source_access_modes),
    });
  return norm(a) === norm(b);
}

function KBConfigPage() {
  const { tenantId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [tenantOpen, setTenantOpen] = useState(false);

  const { data: tenants, isLoading: tenantsLoading, failures: tenantFailures } = useAllTenants();

  const sortedTenants = useMemo(
    () =>
      [...(tenants ?? [])].sort((a, b) => a.company.localeCompare(b.company)),
    [tenants],
  );
  const selectedTenant = tenants?.find((t) => t.id === tenantId);

  return (
    <TooltipProvider>
      <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-auto p-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">KB Config</h1>
          <p className="text-muted-foreground">
            Configure how the SDA accesses a customer&apos;s knowledge base.
          </p>
        </div>

        {/* Context strip: which tenant is being configured */}
        <div className="bg-muted/30 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-lg border p-4">
          <div className="space-y-1.5">
            <p className="text-muted-foreground/70 text-[10px] font-semibold uppercase tracking-widest">
              Tenant
            </p>
            <Popover open={tenantOpen} onOpenChange={setTenantOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tenantOpen}
                  className="bg-background w-[240px] justify-between"
                >
                  <span className="truncate">
                    {selectedTenant
                      ? tenantLabel(selectedTenant)
                      : "Select a tenant..."}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[240px] p-0">
                <Command>
                  <CellTenantsUnavailable failures={tenantFailures} />
                  <CommandInput placeholder="Search tenants..." />
                  <CommandList>
                    <CommandEmpty>
                      {tenantsLoading ? "Loading..." : "No tenant found."}
                    </CommandEmpty>
                    <CommandGroup>
                      {sortedTenants.map((tenant) => (
                        <CommandItem
                          key={tenant.id}
                          value={tenantOptionValue(tenant)}
                          onSelect={() => {
                            void navigate({ search: { tenantId: tenant.id } });
                            setTenantOpen(false);
                          }}
                          className={cn(tenantId === tenant.id && "bg-accent")}
                        >
                          {tenantLabel(tenant)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {!tenantId ? (
          <div className="border-muted-foreground/25 text-muted-foreground flex h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-sm">
            <Info className="h-5 w-5 opacity-60" />
            Select a tenant to configure its knowledge base.
          </div>
        ) : (
          <KBConfigEditor key={tenantId} tenantId={tenantId} />
        )}
      </div>
    </TooltipProvider>
  );
}

function KBConfigEditor({ tenantId }: { tenantId: string }) {
  const {
    data: config,
    isLoading: configLoading,
    isError: configError,
    refetch: refetchConfig,
  } = useKBConfig(tenantId);
  const {
    data: scopeKeys,
    isLoading: scopeKeysLoading,
    isError: scopeKeysError,
  } = useKBScopeKeys(tenantId);
  const { data: kbIntegrations } = useKBIntegrations(tenantId);
  const {
    data: sources,
    isLoading: sourcesLoading,
    isError: sourcesError,
  } = useKBSources(tenantId);
  const upsert = useUpdateKBConfig(tenantId);

  // The tenant-wide access mode is the default for every permission-aware
  // source, so show it when any is connected.
  const showPrivacy = Boolean(kbIntegrations?.hasPermissionAware);
  // Public scope keys are Confluence-only — only Confluence tags documents with
  // the space_key the SDA filters on.
  const showScopeKeys = Boolean(kbIntegrations?.hasConfluence);

  const [draft, setDraft] = useState<KBConfig | null>(null);
  const [baseline, setBaseline] = useState<KBConfig | null>(null);

  // Seed the draft on first load only. Guarding on `baseline === null` prevents
  // a background refetch (window focus / reconnect) from stomping in-progress
  // edits. `key={tenantId}` on the parent resets this state per tenant.
  useEffect(() => {
    if (config && baseline === null) {
      setDraft(config);
      setBaseline(config);
    }
  }, [config, baseline]);

  const dirty = useMemo(
    () => Boolean(draft && baseline && !sameConfig(draft, baseline)),
    [draft, baseline],
  );

  if (configError && !draft) {
    return (
      <div className="border-destructive/30 text-muted-foreground flex h-56 flex-col items-center justify-center gap-3 rounded-lg border border-dashed text-sm">
        <AlertCircle className="text-destructive h-5 w-5" />
        Failed to load KB configuration.
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetchConfig()}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (configLoading || !draft) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  const setAccessMode = (mode: KBConfig["access_mode"]) => {
    setDraft((current) =>
      current
        ? {
            ...current,
            access_mode: mode,
            // Public scope keys exempt spaces from per-user filtering, which is
            // only meaningful in private mode. Switching to public makes them
            // irrelevant, so clear them rather than persist hidden selections.
            public_scope_keys:
              mode === "public" ? [] : current.public_scope_keys,
          }
        : current,
    );
  };

  const setSourceAccess = (sourceId: string, choice: SourceAccessChoice) => {
    setDraft((current) => {
      if (!current) return current;
      // "Inherit" is represented by the source's absence from the map, so the
      // source falls back to the tenant-wide access_mode. Rebuild the map
      // without the key rather than mutating/deleting it dynamically.
      const entries = Object.entries(current.source_access_modes).filter(
        ([id]) => id !== sourceId,
      );
      if (choice !== "inherit") entries.push([sourceId, choice]);
      return { ...current, source_access_modes: Object.fromEntries(entries) };
    });
  };

  const toggleScopeKey = (value: string, checked: boolean) => {
    setDraft((current) => {
      if (!current) return current;
      const set = new Set(current.public_scope_keys);
      if (checked) set.add(value);
      else set.delete(value);
      return { ...current, public_scope_keys: [...set] };
    });
  };

  const atLimit = draft.public_scope_keys.length >= MAX_PUBLIC_SCOPE_KEYS;

  const onSave = () => {
    const saved = draft;
    upsert.mutate(saved, {
      onSuccess: () => {
        setBaseline(saved);
        toast.success("KB configuration saved");
      },
      onError: (err) => {
        // Type the body as possibly-undefined so the optional chain is real:
        // a non-JSON / empty error response has no `data` at runtime.
        const detail = (err as AxiosError<{ detail?: string } | undefined>)
          .response?.data?.detail;
        toast.error(detail ?? "Failed to save");
      },
    });
  };

  const onDiscard = () => {
    if (baseline) setDraft(baseline);
  };

  return (
    <div className="flex max-w-3xl flex-col gap-6 pb-20">
      {/* Tenant-wide access mode — the default for every permission-aware
          source. Per-source overrides are below. */}
      {showPrivacy && (
        <section className="space-y-4 rounded-lg border p-6">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">Default access mode</h2>
            <p className="text-muted-foreground text-sm">
              The default for every permission-aware knowledge base (Confluence,
              SharePoint, Simpplr, Workvivo, Freshservice, Google Drive, Guru).
              Controls which content the SDA may surface to employees. Override
              individual sources below.
            </p>
          </div>
          <RadioGroup
            value={draft.access_mode}
            onValueChange={(value) => {
              setAccessMode(value as KBConfig["access_mode"]);
            }}
            className="gap-3"
          >
            <label
              htmlFor="access-private"
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-md border p-4",
                draft.access_mode === "private" &&
                  "border-primary bg-accent/40",
              )}
            >
              <RadioGroupItem
                value="private"
                id="access-private"
                className="mt-0.5"
              />
              <div className="space-y-1">
                <p className="text-sm font-medium">Private</p>
                <p className="text-muted-foreground text-sm">
                  The SDA respects each employee&apos;s permissions and only
                  surfaces knowledge-base content they are allowed to see. This
                  is the safest default.
                </p>
                <p className="text-muted-foreground text-xs">
                  For Confluence, IDP groups must be pushed to Harmony to supply
                  permissions. The SDA will then answer only with articles the
                  current user has permission to see.
                </p>
              </div>
            </label>
            <label
              htmlFor="access-public"
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-md border p-4",
                draft.access_mode === "public" && "border-primary bg-accent/40",
              )}
            >
              <RadioGroupItem
                value="public"
                id="access-public"
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Public</p>
                <p className="text-muted-foreground text-sm">
                  The SDA may surface any knowledge-base content to any employee,
                  bypassing per-user access filtering entirely. Use only when
                  the whole knowledge base is safe to share broadly.
                </p>
              </div>
            </label>
          </RadioGroup>
        </section>
      )}

      {/* Public scope keys — exemptions from per-user filtering, so only
          relevant for Confluence in private mode */}
      {showScopeKeys && draft.access_mode === "private" && (
        <section className="space-y-4 rounded-lg border p-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Public scope keys</h2>
              <span className="bg-accent text-muted-foreground rounded-full px-2 py-0.5 text-xs font-medium">
                Confluence
              </span>
            </div>
            <p className="text-muted-foreground text-sm">
              Even in private mode, the SDA treats these Confluence spaces as
              public and surfaces them to any employee, bypassing per-user
              access filtering (up to {MAX_PUBLIC_SCOPE_KEYS}).
            </p>
          </div>
          {scopeKeysLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : scopeKeysError ? (
            <p className="text-destructive text-sm">
              Failed to load scope keys. Try again later.
            </p>
          ) : scopeKeys && scopeKeys.length > 0 ? (
            <div className="space-y-3">
              {scopeKeys.map((option) => {
                const checked = draft.public_scope_keys.includes(option.value);
                return (
                  <label
                    key={option.value}
                    htmlFor={`scope-${option.value}`}
                    className="flex cursor-pointer items-center gap-3"
                  >
                    <Checkbox
                      id={`scope-${option.value}`}
                      checked={checked}
                      disabled={!checked && atLimit}
                      onCheckedChange={(state) => {
                        toggleScopeKey(option.value, state === true);
                      }}
                    />
                    <span className="text-sm">
                      {option.label}
                      <span className="text-muted-foreground ml-2 text-xs">
                        {option.provider}
                      </span>
                    </span>
                  </label>
                );
              })}
              {atLimit && (
                <p className="text-muted-foreground text-xs">
                  Maximum of {MAX_PUBLIC_SCOPE_KEYS} scope keys selected.
                </p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              This tenant has no knowledge-base integrations with selectable
              spaces.
            </p>
          )}
        </section>
      )}

      {/* Per-source access — override the tenant-wide default for individual
          permission-aware sources. */}
      {showPrivacy && (
        <section className="space-y-4 rounded-lg border p-6">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">Per-source access</h2>
            <p className="text-muted-foreground text-sm">
              Override the default access mode for individual sources. Sources
              left on <span className="font-medium">Inherit</span> follow the
              default above ({draft.access_mode}).
            </p>
          </div>
          {sourcesLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : sourcesError ? (
            <p className="text-destructive text-sm">
              Failed to load sources. Try again later.
            </p>
          ) : sources && sources.length > 0 ? (
            <div className="divide-y rounded-md border">
              {sources.map((source) => {
                const choice: SourceAccessChoice =
                  draft.source_access_modes[source.id] ?? "inherit";
                return (
                  <div
                    key={source.id}
                    className="flex items-center justify-between gap-4 p-3"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {source.name}
                      </span>
                      <Badge variant="secondary">
                        {PROVIDER_LABELS[source.provider] ?? source.provider}
                      </Badge>
                    </div>
                    <Select
                      value={choice}
                      onValueChange={(value) => {
                        setSourceAccess(source.id, value as SourceAccessChoice);
                      }}
                    >
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">
                          Inherit ({draft.access_mode})
                        </SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="public">Public</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              This tenant has no permission-aware knowledge bases to override.
            </p>
          )}
        </section>
      )}

      {/* Show sources */}
      <section className="space-y-4 rounded-lg border p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="show-sources" className="text-lg font-semibold">
              Show sources
            </Label>
            <p className="text-muted-foreground text-sm">
              When enabled, the SDA includes a link to the source knowledge-base
              article in its answers, so employees can open the original
              document.
            </p>
          </div>
          <Switch
            id="show-sources"
            checked={draft.show_sources}
            onCheckedChange={(checked) => {
              setDraft((current) =>
                current ? { ...current, show_sources: checked } : current,
              );
            }}
          />
        </div>
      </section>

      {dirty && (
        <div className="bg-background/95 fixed inset-x-0 bottom-0 z-20 border-t backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3">
            <span className="text-muted-foreground flex items-center gap-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Unsaved changes
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onDiscard}
                disabled={upsert.isPending}
              >
                Discard
              </Button>
              <Button
                size="sm"
                onClick={onSave}
                disabled={upsert.isPending}
              >
                {upsert.isPending && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                )}
                Save changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
